import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { EN_PLANS as PLANS } from "@/lib/insurance-va-plans";
import { useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Info, Maximize2, Printer, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageSection } from "@/components/site/PageSection";
import { LegacyInsuranceHashRedirect } from "@/components/site/LegacyInsuranceHashRedirect";
import { CheckpointPhoto } from "@/components/site/CheckpointPhoto";
import {
  Callout,
  CurrencyNote,
  FaqList,
  H2,
  H3,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "Zepbound & Wegovy Coverage in Virginia | Virginia Bariatric";
const DESCRIPTION =
  "How insurers decide coverage for Zepbound, Wegovy, and Saxenda — the two checkpoints, prior authorization basics, and a plan-by-plan comparison for Virginia.";
const URL = `${SITE}/medical-weight-loss/insurance`;
const NAME = "Weight-Loss Medication Coverage in Virginia";
const REVIEWED = "August 2026";
const REVIEWED_ISO = "2026-08-01";

const BRANDS: { drug: string; wl: string; approved: string; other: string }[] = [
  {
    drug: "Tirzepatide",
    wl: "Zepbound (injection, pen, KwikPen, vial)",
    approved:
      "Chronic weight management; moderate-to-severe obstructive sleep apnea in adults with obesity",
    other: "Mounjaro — type 2 diabetes (adults and children 10+)",
  },
  {
    drug: "Semaglutide",
    wl: "Wegovy (injection and tablet); Wegovy HD",
    approved:
      "Chronic weight management; cardiovascular risk reduction in established heart disease; MASH with F2–F3 fibrosis",
    other:
      "Ozempic (injection and, since May 2026, tablets) — type 2 diabetes, plus cardiovascular and kidney protection. Rybelsus — type 2 diabetes",
  },
  {
    drug: "Liraglutide",
    wl: "Saxenda (and generic liraglutide)",
    approved: "Chronic weight management, ages 12 and up",
    other: "Victoza — type 2 diabetes",
  },
];

const FAQS: Faq[] = [
  {
    q: "Will my insurance cover Zepbound or Wegovy in Virginia?",
    a: "It depends on two separate checkpoints. First, does your plan cover weight-loss medications at all? Many Virginia employer plans, and all individual and small-group marketplace plans, exclude them outright. Second, if the category is covered, do you meet the plan's clinical prior authorization criteria — usually a BMI of 30 or higher, or 27 or higher with a weight-related condition, plus documented lifestyle efforts. Ask your plan about the exclusion question first, because no documentation can overcome a benefit exclusion.",
  },
  {
    q: "My plan excludes weight-loss drugs. Are there other ways to get covered?",
    a: "Yes. Some patients have a backdoor available to them. Because the same molecule is sold under different brand names for different conditions, a documented diagnosis of type 2 diabetes, obstructive sleep apnea, established cardiovascular disease, or MASH liver disease can make the medication covered under a non-weight-loss indication. See the backdoor section on this page.",
  },
  {
    q: "Does Virginia Medicaid cover weight-loss medication?",
    a: "Virginia Medicaid and the Anthem HealthKeepers Plus managed care plans use a service authorization process for weight-management drugs. Coverage exists, but it is conditional: expect BMI thresholds, documented lifestyle participation, and periodic reauthorization based on weight loss achieved. Submit through the DMAS Weight-Loss Management service authorization form your prescriber completes.",
  },
  {
    q: "Does the Virginia state employee health plan cover Zepbound or Wegovy?",
    a: "The state employee plan has covered anti-obesity medication with prior authorization, but the Department of Human Resource Management has been tightening the terms as costs rise. Check the current open enrollment materials for your plan year before assuming last year's rules still apply, and confirm which drugs are on the formulary rather than which are merely 'covered.'",
  },
  {
    q: "Does Medicare cover Wegovy or Zepbound in Virginia?",
    a: "Medicare Part D still excludes drugs used solely for weight loss. It does cover these same molecules when prescribed for an approved non-weight-loss indication — semaglutide for cardiovascular risk reduction, tirzepatide for moderate-to-severe obstructive sleep apnea, or the diabetes-labeled versions for type 2 diabetes. That is why a sleep study or an A1c can change the answer entirely.",
  },
  {
    q: "How long does prior authorization take in Virginia?",
    a: "Most commercial plans decide a standard pharmacy prior authorization within a few business days once the complete packet is received; expedited requests are faster. Delays are almost always missing documentation — weight history, BMI, or proof of lifestyle participation — rather than a slow reviewer.",
  },
  {
    q: "My prior authorization was denied. What are my appeal rights?",
    a: "Read the denial letter and find the exact criterion cited. If it is a clinical criterion, an internal appeal with the missing documentation often succeeds. If the internal appeal fails, fully insured Virginia plans go to external review through the Virginia Bureau of Insurance; self-funded employer plans follow federal ERISA external review. If the denial is a benefit exclusion rather than a clinical criterion, appeals rarely work — the backdoor route is the better path.",
  },
  {
    q: "What if I live in Virginia but my employer plan is based in another state?",
    a: "Your benefit terms follow the plan document, not your address. A self-funded plan headquartered elsewhere applies its own formulary and exclusions to you in Virginia. Ask HR for the summary plan description and the pharmacy formulary, and check the exclusion question before anything else.",
  },
  {
    q: "Do marketplace plans in Virginia cover weight-loss medication?",
    a: "Almost never. Virginia's individual and small-group plans sold on the ACA marketplace follow the state's essential health benefits benchmark, which does not include anti-obesity medication. Some carriers sell an optional rider or a separate weight-management program, but the standard plan documents exclude these drugs. If you are shopping during open enrollment and medication coverage matters to you, read the pharmacy exclusions list before you compare premiums.",
  },
  {
    q: "Does TRICARE cover Zepbound or Wegovy for Virginia military families?",
    a: "TRICARE does cover FDA-approved weight-management medication through the Express Scripts pharmacy benefit, with prior authorization and step requirements — typically a documented BMI threshold and participation in a lifestyle program. Rules and preferred agents change with the formulary, so have your prescriber confirm the current criteria before starting.",
  },
  {
    q: "Is bariatric surgery covered when medication is not?",
    a: "Often, yes — and it surprises people. Many Virginia employer plans exclude anti-obesity drugs while still covering gastric sleeve or gastric bypass surgery as a medical benefit, because surgery is priced as a one-time procedure rather than an ongoing pharmacy cost. If your medication request is denied on a benefit exclusion, it is worth checking your surgical benefit before assuming nothing is covered.",
  },
  {
    q: "How much do these medications cost without insurance in Virginia?",
    a: "Manufacturer direct-pay programs and pharmacy cash prices have brought self-pay costs well below list price, and the number moves often. We review current self-pay options with patients at the visit so the comparison is based on this month's pricing rather than an outdated figure.",
  },
];


export const Route = createFileRoute("/medical-weight-loss/insurance/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-medications-va.png" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { property: "og:image:alt", content: "Will insurance cover weight-loss medication? Virginia plan-by-plan coverage rules for Zepbound, Wegovy, and Saxenda — Virginia Bariatric Surgery" },
        { name: "twitter:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-medications-va.png" },
        { name: "twitter:image:alt", content: "Will insurance cover weight-loss medication? Virginia plan-by-plan coverage rules for Zepbound, Wegovy, and Saxenda — Virginia Bariatric Surgery" },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance") : []),
        ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({
            url: URL,
            name: NAME,
            description: DESCRIPTION,
            hasFaq: true,
            audienceType: "Virginia patients seeking weight-loss medication coverage",
            lastReviewed: REVIEWED_ISO,
          })
        ),
      },
    ] : [],
  }),
  component: InsuranceHub,
  pendingComponent: InsuranceHubPending,
});

function InsuranceHubPending() {
  return (
    <>
      <section className="mx-auto max-w-7xl px-4 pt-8 pb-4 text-center">
        <div className="mx-auto h-9 w-3/4 max-w-2xl animate-pulse rounded bg-muted md:h-14" />
        <div className="mx-auto mt-4 h-5 w-5/6 max-w-3xl animate-pulse rounded bg-muted" />
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-16">
        <div className="space-y-4">
          <div className="h-6 w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
        </div>

        <div className="mt-8 space-y-3">
          <div className="h-5 w-1/3 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
        </div>

        <div className="mt-8 h-48 w-full animate-pulse rounded bg-muted" />

        <div className="mt-8 space-y-3">
          <div className="h-5 w-1/3 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
        </div>
      </section>
    </>
  );
}

function TooltipCell({
  children,
  note,
  expanded = false,
}: {
  children: React.ReactNode;
  note?: string;
  expanded?: boolean;
}) {
  if (!note) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex max-w-full cursor-help items-center gap-1"
          tabIndex={0}
        >
          <span className="break-words">{children}</span>
          <Info
            className={`shrink-0 text-brand-deep opacity-70 hover:opacity-100 focus:opacity-100 ${expanded ? "h-4 w-4" : "h-3.5 w-3.5"}`}
            aria-hidden="true"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        sideOffset={6}
        className="max-w-xs text-xs leading-snug"
      >
        {note}
      </TooltipContent>
    </Tooltip>
  );
}

function PlansTable({ expanded = false }: { expanded?: boolean }) {
  return (
    <table className={`plans-at-a-glance-table w-full border-collapse ${expanded ? "min-w-[64rem] text-base" : "table-fixed text-sm"}`}>
      <caption className="sr-only">
        Prior authorization criteria for weight-loss medication by Virginia plan
      </caption>
      <thead>
        <tr className="bg-brand-soft/60 text-left">
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[16%]" : "p-2.5 w-[18%]"}`}>Plan</th>
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[20%]" : "p-2.5 w-[18%]"}`}>BMI required</th>
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[22%]" : "p-2.5 w-[20%]"}`}>Lifestyle requirement</th>
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[16%]" : "p-2.5 w-[16%]"}`}>Must try another drug first?</th>
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[13%]" : "p-2.5 w-[14%]"}`}>Initial approval</th>
          <th scope="col" className={`border border-border ${expanded ? "p-4 w-[13%]" : "p-2.5 w-[14%]"}`}>Renewal</th>
        </tr>
      </thead>
      <tbody>
        {PLANS.map((p) => (
          <tr key={p.plan} className="align-top">
            <th scope="row" className={`border border-border text-left font-medium break-words ${expanded ? "p-4" : "p-2.5"}`}>
              {p.plan}
            </th>
            <td className={`border border-border break-words ${expanded ? "p-4" : "p-2.5"}`}>
              <TooltipCell note={p.bmiNote} expanded={expanded}>{p.bmi}</TooltipCell>
            </td>
            <td className={`border border-border break-words ${expanded ? "p-4" : "p-2.5"}`}>
              <TooltipCell note={p.lifestyleNote} expanded={expanded}>{p.lifestyle}</TooltipCell>
            </td>
            <td className={`border border-border break-words ${expanded ? "p-4" : "p-2.5"}`}>
              <TooltipCell note={p.stepNote} expanded={expanded}>{p.step}</TooltipCell>
            </td>
            <td className={`border border-border break-words ${expanded ? "p-4" : "p-2.5"}`}>
              <TooltipCell note={p.initialNote} expanded={expanded}>{p.initial}</TooltipCell>
            </td>
            <td className={`border border-border break-words ${expanded ? "p-4" : "p-2.5"}`}>
              <TooltipCell note={p.renewalNote} expanded={expanded}>{p.renewal}</TooltipCell>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function openPrintWindow() {
  if (typeof window === "undefined") return;

  const tableHtml = document.querySelector(".print-only-section .plans-at-a-glance-table")?.outerHTML ?? "";
  const printHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Virginia plans at a glance | Virginia Bariatric</title>
  <style>
    @page { margin: 0.5in; }
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: #000;
      background: #fff;
      margin: 0;
      padding: 0;
    }
    h1 { font-size: 1.25rem; margin: 0 0 0.5rem; }
    p { font-size: 0.875rem; margin: 0 0 1rem; }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 0.8rem;
    }
    th, td {
      border: 1px solid #000;
      padding: 0.375rem;
      text-align: left;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    th { background: #e8f4fc !important; font-weight: 600; }
    tr { break-inside: avoid; page-break-inside: avoid; }
  </style>
</head>
<body>
  <h1>Virginia plans at a glance</h1>
  <p>Criteria below are drawn from each plan&rsquo;s current published authorization documents. Employer plans can and do vary from their carrier&rsquo;s standard policy.</p>
  ${tableHtml}
  <script>
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 200);
    });
  <\/script>
</body>
</html>
  `.trim();

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();
}

function InsuranceHub() {
  const [expanded, setExpanded] = useState(false);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePlan, setMobilePlan] = useState<string>(PLANS[0].plan);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);



  return (
    <>
      <LegacyInsuranceHashRedirect />
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Weight-Loss Medication Coverage in Virginia
        </h1>

        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          What different insurers require &mdash; and the backdoor most people miss.
        </p>
        <TelehealthCta />


        <nav aria-label="Choose your state" className="mt-5 flex justify-center">
          <Link
            to="/medical-weight-loss/insurance/nc"
            preload="intent"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            North Carolina resident? See NC coverage rules
          </Link>
        </nav>


        <p className="mt-4 text-sm text-muted-foreground">
          Reviewed by{" "}
          <Link to="/about" className="text-brand-deep underline hover:no-underline">
            Matthew Fitzer, MD, FACS
          </Link>{" "}
          &middot; Last reviewed{" "}
          <time dateTime={REVIEWED_ISO}>{REVIEWED}</time>
        </p>
      </PageSection>


      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote />

        <H2 id="two-gates">Start here: the front entrance versus the backdoor</H2>
        <P>
          So, you&rsquo;re wondering &mdash; &ldquo;will my insurance cover Zepbound?&rdquo; Good
          question. There are two ways to get weight-loss medication covered by your insurer, and
          we think of them as a front entrance and a backdoor. Most patients who are prescribed{" "}
          <Link
            to="/medical-weight-loss/medications"
            className="text-brand-deep underline hover:no-underline"
          >
            weight-loss medication
          </Link>{" "}
          are forced to try the front entrance. It consists of two checkpoints, and you must clear
          both to have your medication covered.
        </P>

        <CheckpointPhoto />

        <H3>Checkpoint 1: Does your plan cover weight-loss drugs at all?</H3>
        <P>
          Some do, and some don&rsquo;t. It&rsquo;s a benefits question, decided by whoever runs your
          plan &mdash; usually your employer. Many plans exclude weight-loss medications entirely.
          When that&rsquo;s the case, no amount of documentation from your doctor will get the drug
          covered, because there&rsquo;s nothing to approve. Cigna&rsquo;s own policy states
          plainly that &ldquo;weight loss medications are specifically excluded under many benefit
          plans.&rdquo; UnitedHealthcare describes its weight-loss coverage program as &ldquo;an
          optional program that is put in place for clients or businesses that have elected to
          cover weight loss products.&rdquo;
        </P>

        <H3>Checkpoint 2: Do you meet the clinical criteria?</H3>
        <P>
          If your plan does cover the drug you want, you are in luck. Now you must prove that you
          meet the medical criteria to be on it, which means prior authorization. Your doctor
          submits documentation, and the plan decides whether you qualify.
        </P>

        <Callout>
          <strong>Dr. Fitzer recommendation:</strong> Start by asking your insurer whether your
          policy covers any injectable weight loss meds. Some policies don&rsquo;t. If yours
          doesn&rsquo;t, insurance will be a dead end.

          <p className="mt-3">
            We <em>could</em> try to prescribe you an injectable anyway. Your pharmacy will check
            with your insurer, who will decline to cover it at that point. You&rsquo;ll get a denial
            letter in the mail or by email.
          </p>

          <p className="mt-3">
            You can appeal any kind of denial, but no-coverage appeals are a dead end. Trust me,
            I&rsquo;ve tried.
            <sup className="text-brand">
              <a
                href="#footnote-1"
                id="footnote-ref-1"
                aria-label="Footnote 1"
                className="underline"
              >
                1
              </a>
            </sup>{" "}
            Use your time and energy to find plan B.
          </p>
        </Callout>





        <H2 id="your-plan">Find your plan</H2>
        <P>
          Coverage rules differ enormously by payor. Start with the guide that
          matches your situation, or read the fundamentals above first.
        </P>
        <SpokeCards />

        <H2 id="building-blocks">The building blocks of prior authorization</H2>
        <P>
          Nearly every Virginia plan builds its criteria from the same set of pieces. What differs
          is which pieces they use and how strict each one is.
        </P>
        <UL>
          <li>
            <strong>BMI threshold.</strong> The most common standard is 30 or higher, or 27 or
            higher with a weight-related condition. Some Virginia plans set the bar considerably
            higher.
          </li>
          <li>
            <strong>A qualifying weight-related condition.</strong> Typically high blood pressure,
            type 2 diabetes, high cholesterol, sleep apnea, heart disease, knee osteoarthritis,
            fatty liver disease, PCOS, asthma, or COPD.
          </li>
          <li>
            <strong>A documented lifestyle attempt.</strong> Commonly 3 to 6 months of diet,
            exercise, and behavioral changes recorded in your chart. This is the requirement that
            most often trips people up &mdash; it must be documented, not just remembered.
          </li>
          <li>
            <strong>Step therapy.</strong> Some plans require you to try and fail a cheaper, older
            weight-loss drug first. Virginia Medicaid and TRICARE both require this.
          </li>
          <li>
            <strong>Age minimums.</strong> Zepbound is 18 and older. Wegovy injection and Saxenda
            can be approved starting at 12 in some plans.
          </li>
          <li>
            <strong>Proof it&rsquo;s working.</strong> Almost every plan requires documented weight
            loss &mdash; usually at least 5% of your starting weight &mdash; before renewing.
            Saxenda is often held to 4% instead.
          </li>
          <li>
            <strong>Not doubling up.</strong> No plan will approve two GLP-1 medications at once.
          </li>
        </UL>

        <H2 id="plans">Virginia plans at a glance</H2>
        <P>
          Criteria below are drawn from each plan&rsquo;s current published authorization
          documents. Employer plans can and do vary from their carrier&rsquo;s standard policy.
        </P>

        <div data-testid="plans-at-a-glance">
        <TooltipProvider delayDuration={150}>
          {/* Desktop table */}
          <div className="mt-5 hidden md:block print:hidden">
          <div className="relative">
            <div className="absolute -top-3 -right-3 z-10 flex items-center gap-2">
              <button
                type="button"
                onClick={openPrintWindow}
                className="inline-flex items-center gap-1.5 rounded-full bg-background border border-border p-2 shadow-sm text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2"
                aria-label="Print Virginia plans at a glance"
              >
                <Printer className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Print</span>
              </button>
              <button
                type="button"
                ref={expandButtonRef}
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-background border border-border p-2 shadow-sm text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2"
                aria-label="Expand Virginia plans at a glance table to full width"
              >
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Expand</span>
              </button>
            </div>
            <PlansTable />
          </div>
        </div>

        {/* Print-only full-width table */}
        <div className="print-only-section hidden print:block">
          <h3 className="mb-4 text-2xl font-bold text-black">Virginia plans at a glance</h3>
          <p className="mb-4 text-sm text-black">
            Criteria below are drawn from each plan&rsquo;s current published authorization
            documents. Employer plans can and do vary from their carrier&rsquo;s standard policy.
          </p>
          <PlansTable expanded />
        </div>

        {/* Expanded full-width modal (Radix: focus trap, Escape, focus restore) */}
        <DialogPrimitive.Root open={expanded} onOpenChange={setExpanded}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                requestAnimationFrame(() => expandButtonRef.current?.focus());
              }}
              className="fixed inset-0 z-50 overflow-auto p-4 focus:outline-none"
            >
              <div className="mx-auto w-full max-w-[90rem] bg-background p-4 md:p-8 shadow-2xl rounded-lg border border-border">
                <div className="mb-4 flex items-center justify-between">
                  <DialogPrimitive.Title className="text-xl font-semibold text-foreground">
                    Virginia plans at a glance
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Close className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2">
                    <X className="h-4 w-4" aria-hidden="true" />
                    Close
                  </DialogPrimitive.Close>
                </div>
                <p className="mb-4 text-sm text-muted-foreground">
                  Criteria below are drawn from each plan&rsquo;s current published authorization
                  documents. Employer plans can and do vary from their carrier&rsquo;s standard policy.
                </p>
                <div className="overflow-x-auto">
                  <PlansTable expanded />
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>


        {/* Mobile cards */}
        <div className="mt-5 space-y-3 md:hidden print:hidden">
          <button
            type="button"
            ref={mobileButtonRef}
            onClick={() => {
              setMobilePlan(PLANS[0].plan);
              setMobileOpen(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-brand-soft/40 px-3 py-2.5 text-sm font-medium text-brand-deep hover:bg-brand-soft/70 focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
            Compare all plans full screen
          </button>

          {PLANS.map((p) => (
            <div key={p.plan} className="rounded border border-border p-3 text-sm">
              <h3 className="font-medium text-base mb-2">{p.plan}</h3>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground">BMI required</dt>
                <dd><TooltipCell note={p.bmiNote}>{p.bmi}</TooltipCell></dd>
                <dt className="text-muted-foreground">Lifestyle</dt>
                <dd><TooltipCell note={p.lifestyleNote}>{p.lifestyle}</TooltipCell></dd>
                <dt className="text-muted-foreground">Step therapy</dt>
                <dd><TooltipCell note={p.stepNote}>{p.step}</TooltipCell></dd>
                <dt className="text-muted-foreground">Initial approval</dt>
                <dd><TooltipCell note={p.initialNote}>{p.initial}</TooltipCell></dd>
                <dt className="text-muted-foreground">Renewal</dt>
                <dd><TooltipCell note={p.renewalNote}>{p.renewal}</TooltipCell></dd>
              </dl>
            </div>
          ))}
        </div>

        {/* Mobile full-screen plan viewer */}
        <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background md:hidden" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                requestAnimationFrame(() => mobileButtonRef.current?.focus());
              }}
              className="fixed inset-0 z-50 flex h-dvh w-screen flex-col bg-background focus:outline-none md:hidden"
            >
              <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
                <DialogPrimitive.Title className="min-w-0 truncate text-base font-semibold text-foreground">
                  Virginia plans at a glance
                </DialogPrimitive.Title>
                <DialogPrimitive.Close
                  aria-label="Close full-screen plan comparison"
                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md border border-border text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </DialogPrimitive.Close>
              </header>

              <nav
                aria-label="Choose a plan"
                className="shrink-0 overflow-x-auto border-b border-border px-4 py-2"
              >
                <ul className="flex w-max gap-2">
                  {PLANS.map((p) => (
                    <li key={p.plan}>
                      <button
                        type="button"
                        onClick={() => setMobilePlan(p.plan)}
                        aria-current={mobilePlan === p.plan ? "true" : undefined}
                        className={`whitespace-nowrap rounded-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2 ${
                          mobilePlan === p.plan
                            ? "border-brand-deep bg-brand-deep text-white"
                            : "border-border bg-background text-foreground"
                        }`}
                      >
                        {p.plan}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                {PLANS.filter((p) => p.plan === mobilePlan).map((p) => (
                  <article key={p.plan}>
                    <h3 className="mb-3 text-lg font-semibold text-brand-deep">{p.plan}</h3>
                    <dl className="divide-y divide-border border-y border-border">
                      {(
                        [
                          ["BMI required", p.bmi, p.bmiNote],
                          ["Lifestyle requirement", p.lifestyle, p.lifestyleNote],
                          ["Must try another drug first?", p.step, p.stepNote],
                          ["Initial approval", p.initial, p.initialNote],
                          ["Renewal", p.renewal, p.renewalNote],
                        ] as const
                      ).map(([label, value, note]) => (
                        <div key={label} className="py-3">
                          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {label}
                          </dt>
                          <dd className="mt-1 text-base leading-snug text-foreground">{value}</dd>
                          {note ? (
                            <p className="mt-1 text-sm text-muted-foreground">{note}</p>
                          ) : null}
                        </div>
                      ))}
                    </dl>
                    <p className="mt-4 text-xs text-muted-foreground">
                      Employer plans can and do vary from their carrier&rsquo;s standard policy.
                    </p>
                  </article>
                ))}
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>

        </TooltipProvider>
        </div>


        <H2 id="timing">A note on timing</H2>
        <P>Several Virginia coverage rules changed on July 1, 2026, and more changes are scheduled:</P>
        <UL>
          <li>
            <strong>July 1, 2026</strong> &mdash; state employee plans raised the BMI requirement to
            35 and added a pharmacy deductible; Virginia Medicaid&rsquo;s updated authorization form
            took effect; the Medicare GLP-1 Bridge launched; UnitedHealthcare&rsquo;s updated
            commercial criteria took effect
          </li>
          <li>
            <strong>October 1, 2026</strong> &mdash; Zepbound returns to CVS Caremark&rsquo;s
            standard formulary
          </li>
          <li>
            <strong>Plan year 2027</strong> &mdash; OPM expects FEHB carriers to require intensive
            behavioral therapy for anti-obesity medication coverage
          </li>
          <li>
            <strong>Plan year 2028</strong> &mdash; Virginia&rsquo;s new Essential Health Benefits
            benchmark plan application is pending with CMS
          </li>
        </UL>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="backdoor">The backdoor: coverage follows the diagnosis, not the drug</H2>
        <P>
          Here&rsquo;s the thing almost nobody explains: the same medicine is sold under different
          brand names for different conditions, and your insurance treats those brand names
          completely differently. Tirzepatide is tirzepatide &mdash; but packaged as{" "}
          <strong>Zepbound</strong> it&rsquo;s a weight-loss drug, or sometimes a sleep apnea
          medicine. Packaged as <strong>Mounjaro</strong>, it&rsquo;s a diabetes drug. Semaglutide
          is semaglutide, but as <strong>Wegovy</strong> it&rsquo;s for weight loss, and as{" "}
          <strong>Ozempic</strong> it&rsquo;s for type 2 diabetes. Identical molecules, different
          labels.
        </P>
        <P>
          This matters enormously, because when a plan excludes &ldquo;weight-loss drugs,&rdquo;
          that exclusion is written against the <em>indication</em> &mdash; the condition to be
          treated &mdash; not the molecule. Injectable diabetes medications, including semaglutide
          and tirzepatide, are covered under essentially every plan in Virginia, including Medicare
          Part D, which excludes weight-loss drugs outright.
        </P>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[42rem] border-collapse text-sm">
            <caption className="sr-only">
              Weight-loss brand names and the same active drug sold for other conditions
            </caption>
            <thead>
              <tr className="bg-brand-soft/60 text-left">
                <th scope="col" className="border border-border p-3">Active drug</th>
                <th scope="col" className="border border-border p-3">Weight-loss brand</th>
                <th scope="col" className="border border-border p-3">What it&rsquo;s approved to treat</th>
                <th scope="col" className="border border-border p-3">Same drug, other brand(s)</th>
              </tr>
            </thead>
            <tbody>
              {BRANDS.map((b) => (
                <tr key={b.drug} className="align-top">
                  <th scope="row" className="border border-border p-3 text-left font-medium">
                    {b.drug}
                  </th>
                  <td className="border border-border p-3">{b.wl}</td>
                  <td className="border border-border p-3">{b.approved}</td>
                  <td className="border border-border p-3">{b.other}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <H3>The four backdoors</H3>
        <UL>
          <li>
            <strong>Type 2 diabetes &rarr; Mounjaro or Ozempic.</strong> If you have T2DM, these
            are covered under standard pharmacy benefits, usually with far easier prior
            authorization than the weight-loss versions. This is not a workaround &mdash; it is the
            clinically correct prescription for someone with diabetes, and it happens to be the
            same active drug.
          </li>
          <li>
            <strong>Obstructive sleep apnea &rarr; Zepbound.</strong> Zepbound carries its own FDA
            approval for moderate-to-severe OSA in adults with obesity &mdash; generally a BMI of
            30 or higher plus a sleep study documenting 15 or more apnea events per hour. Plans
            that exclude weight-loss drugs will often still cover this.
          </li>
          <li>
            <strong>Established cardiovascular disease &rarr; Wegovy.</strong> Wegovy is approved
            to reduce heart attack, stroke, and cardiovascular death in adults with known heart
            disease and obesity or overweight. That is a cardiovascular indication, not a
            weight-loss one.
          </li>
          <li>
            <strong>MASH liver disease &rarr; Wegovy injection.</strong> Approved for non-cirrhotic
            MASH with moderate-to-advanced fibrosis (stage F2 to F3). Most plans require specialist
            involvement and recent imaging or fibrosis testing.
          </li>
        </UL>

        <H3>The payors themselves point this out</H3>
        <P>
          <strong>TRICARE&rsquo;s own authorization form</strong> instructs prescribers: &ldquo;If
          patient is diabetic and meets the prior authorization criteria for Trulicity, Victoza,
          Ozempic, or Mounjaro, please consider these alternatives.&rdquo; The plan is actively
          steering diabetic patients to the diabetes-labeled product.
        </P>
        <P>
          <strong>Medicare does the same thing.</strong> The GLP-1 Bridge prior authorization form
          opens by telling prescribers that if the patient has obstructive sleep apnea, MASH with
          fibrosis, type 2 diabetes, or established cardiovascular disease, the request must go to
          the patient&rsquo;s regular Part D plan instead of the Bridge &mdash; because those
          diagnoses are already Part D&ndash;eligible.
        </P>

        <H3>Why not get evaluated?</H3>
        <P>
          These &ldquo;backdoor&rdquo; conditions are genuinely underdiagnosed in people with
          obesity. Obstructive sleep apnea in particular often goes unrecognized for years &mdash;
          if you snore, wake unrefreshed, or are drowsy during the day, ask about a sleep study. An
          A1c test will tell you where you stand on diabetes. You may already qualify and not know
          it.
        </P>
        <P>A few practical limits to be aware of:</P>
        <UL>
          <li>
            <strong>Prediabetes is not diabetes.</strong> It may help you meet a comorbidity
            requirement for Wegovy or Zepbound, but it does not open the Mounjaro or Ozempic door.
          </li>
          <li>
            <strong>You cannot be on two GLP-1s at once.</strong> Every Virginia plan reviewed here
            blocks concurrent GLP-1 therapy.
          </li>
          <li>
            <strong>Doses aren&rsquo;t identical across brands.</strong> Ozempic tops out at 2 mg
            while Wegovy goes to 2.4 mg (and Wegovy HD to 7.2 mg). Mounjaro and Zepbound both reach
            15 mg.
          </li>
          <li>
            <strong>TRICARE blocks the reverse direction.</strong> Its form states that
            non-FDA-approved uses &ldquo;are not approved including diabetes mellitus&rdquo; &mdash;
            you cannot get Wegovy or Zepbound covered by citing diabetes.
          </li>
          <li>
            <strong>Formulary tiers differ.</strong> Being covered under a diabetes indication
            doesn&rsquo;t guarantee a low copay.
          </li>
        </UL>
        <P>
          <strong>What to say to your doctor:</strong> &ldquo;My plan excludes weight-loss drugs.
          Do I have any other condition &mdash; sleep apnea, diabetes, heart disease, fatty liver
          &mdash; that these medications are approved to treat? And should I be tested for any of
          them?&rdquo;
        </P>

        <H2 id="first-appointment">What to bring to your first appointment</H2>
        <P>
          Approvals move faster when the documentation is ready on day one. If you can, gather:
        </P>
        <UL>
          <li>
            Your insurance card, front and back, plus the name of your employer if your coverage is
            job-based
          </li>
          <li>
            Your plan&rsquo;s prescription drug formulary, if you can get it from HR or the member
            portal
          </li>
          <li>
            Weight history &mdash; highest weight, current weight, and any records of past
            weight-loss attempts
          </li>
          <li>
            A list of your current medications and any diagnoses, especially blood pressure,
            cholesterol, blood sugar, and sleep problems
          </li>
          <li>Recent lab work if you have it, particularly A1c, lipids, and liver enzymes</li>
          <li>
            Any prior denial letters &mdash; these state exactly which criterion was not met, which
            tells us what to fix
          </li>
        </UL>

        <section aria-labelledby="footnotes-heading">
          <h2
            id="footnotes-heading"
            className="mt-8 scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid"
          >
            Notes
          </h2>
          <ol className="mt-2 list-decimal space-y-2 pl-6 text-sm text-foreground/70">
            <li id="footnote-1" className="scroll-mt-24">
              I&rsquo;ve tried many times. I have <em>written</em> reams of letters to support such
              appeals, but they don&rsquo;t succeed. I have successfully appealed loads of denials of
              different kinds, but not those.{" "}
              <a
                href="#footnote-ref-1"
                className="text-brand-deep underline hover:no-underline"
                aria-label="Back to text"
              >
                ↩
              </a>
            </li>
          </ol>
        </section>

        <H2 id="sources">Sources</H2>
        <UL>
          <li>Virginia DMAS Service Authorization Form: Weight-Loss Management, effective 07/01/2026</li>
          <li>
            Anthem HealthKeepers Plus Prior Authorization Form: Weight-Loss Management
            (VABCBS-CD-091232-25)
          </li>
          <li>
            Virginia DHRM, Open Enrollment Spotlight 2026 and State Employee Health Plan Update to
            the Senate Finance and Appropriations Committee, April 14, 2026
          </li>
          <li>
            Sentara Health Plans Pharmacy Prior Authorization/Step-Edit Request, Weight Management
            Drugs (CORE), revised 6/11/2026
          </li>
          <li>Cigna Coverage Policy IP0206, Weight Loss &ndash; GLP-1 Agonists BMI &ge; 30, effective 06/01/2026</li>
          <li>UnitedHealthcare Clinical Pharmacy Program 2026 P 1114-21, effective 7/1/2026</li>
          <li>Aetna Pharmacy Clinical Policy Bulletins, weight-loss and anti-obesity agents</li>
          <li>CMS, Medicare GLP-1 Bridge program page and prior authorization request form</li>
          <li>
            TRICARE Prior Authorization Request Form for semaglutide (Wegovy) and tirzepatide
            (Zepbound Pen Injector), dated 15 July 2026
          </li>
          <li>OPM FEHB carrier guidance, plan years 2026 and 2027</li>
          <li>
            Virginia Joint Commission on Health Care, Obesity and Eating Disorders Prevention and
            Treatment in Virginia; Virginia State Corporation Commission Bureau of Insurance,
            Essential Health Benefits Benchmark Plan
          </li>
        </UL>

      </PageSection>

      <InsuranceCTA />
    </>
  );
}
